import { useEffect, useState } from "react";
import CurvyButton from "@modules/common/components/curvy-button";
import UnderlineLink from "@modules/common/components/underline-link";
import Image from "next/image";
import Link from "next/link";
import { StrapiImage } from "types/image";
import { useTranslation } from "@i18n";

const HERO_IMAGES_EN: readonly string[] = [
  "/hero-1.jpg",
  "/hero-2.jpg",
  "/hero-3.jpg",
  "/hero-4.jpg",
];

const HERO_IMAGES_AR: readonly string[] = [
  "/images/home-1-arabic.png",
  "/images/home-2-arabic.png",
  "/images/home-3-arabic.png",
  "/images/home-4-arabic.png",
];

function Hero({
  data,
}: {
  data: {
    heroSloganPart1: string;
    heroSloganPart2: string;
    heroBody: string;
    heroImages: StrapiImage[];
  };
}) {
  void data;
  const { t, locale } = useTranslation();
  const heroImages = locale === "ar" ? HERO_IMAGES_AR : HERO_IMAGES_EN;

  // Pick a random image after mount so SSR/CSR markup matches (avoids hydration
  // mismatches from Math.random()). The first paint shows index 0 briefly,
  // then swaps to the random one on the client.
  const [src, setSrc] = useState<string>(heroImages[0]);
  useEffect(() => {
    setSrc(heroImages[Math.floor(Math.random() * heroImages.length)]);
  }, [heroImages]);

  return (
    <div className="flex w-screen px-4 lg:px-[4.5rem] lg:h-[80vh] flex-col-reverse lg:flex-row gap-4">
      {/* ---------- LEFT SIDE (TEXT SECTION) ---------- */}
      <div className="w-full lg:w-1/2 h-1/2 text-darkGreen content-center lg:h-auto lg:me-10">
        <h1 className="lg:text-[4vw] lg:text-6xl text-3xl">
          {t.hero.sloganLine1} <br />
          {t.hero.sloganLine2} <br />
          {t.hero.sloganHighlight && (
            <span className="text-primary font-myFont">
              {t.hero.sloganHighlight}
            </span>
          )}
        </h1>

        <p className="text-lightGray text-lg lg:text-[1vw] lg:mt-10 mt-6 lg:w-3/4">
          {t.hero.body}
        </p>

        <div className="flex lg:mt-10 mt-6 gap-6">
          <Link href="/#contact" passHref>
            <CurvyButton className="bg-primary text-terryWhite py-3 px-3 rounded-full w-36 justify-center">
              {t.hero.getStarted}
            </CurvyButton>
          </Link>
          <UnderlineLink
            className="text-darkGreen"
            href="/#products"
            areaLabel={t.hero.viewProducts}
          >
            {t.hero.viewProducts}
          </UnderlineLink>
        </div>
      </div>

      {/* ---------- RIGHT SIDE (SINGLE RANDOM IMAGE) ---------- */}
      {/* No rounded clip on the wrapper — the image files have their own
          rounded corners baked in, so wrapping them in another rounded mask
          would either crop the corners or hide the design. */}
      <div className="w-full lg:w-1/2 relative h-[calc(1.041*(100vw-2rem))] lg:h-auto">
        <Image
          src={src}
          alt="Hero"
          fill
          unoptimized
          priority
          style={{ objectFit: "contain" }}
        />
      </div>
    </div>
  );
}

export default Hero;
